import { Typography } from "antd";
import { Package, Truck, Users, Calendar, Clock } from "lucide-react";
import { FC } from "react";
import dayjs from "dayjs";
import { useNavigate } from 'react-router-dom';
import { TServiceTicket } from "~/types";
import {
  calculateCubicFeetInventory,
  capitalize,
} from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { ServiceType } from "~/utils/constants";

const { Text } = Typography;

interface ContainerInfoListProps {
  serviceTickets: TServiceTicket[];
  showHeader?: boolean;
  maxItems?: number;
  className?: string;
  bookingId?: string; // Add optional booking ID prop
  tempClientData?: { name?: string; email?: string; phone?: string }; // Fallback for quick clients
}

// Helper function to get proper client name (shipper/consignee for the specific service)
const getClientDisplayName = (client: any, ticket?: any) => {
  // Priority: tempLocationData.name > client.companyName > client name
  // NOTE: We intentionally do NOT fall back to booking's tempClientData here because
  // that's the booking CLIENT, not the shipper/consignee for this specific service

  // For estimates, use tempLocationData.name (shipper/consignee contact)
  if (ticket?.tempLocationData?.name) {
    return ticket.tempLocationData.name;
  }

  if (client?.companyName) {
    return client.companyName;
  }

  const contactName = [client?.firstName, client?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (contactName) {
    return contactName;
  }

  // No specific contact for this service - return null to indicate "not specified"
  return null;
};

// Helper function to format full address properly (with tempLocationData fallback)
const formatServiceAddress = (location: any, ticket?: any) => {
  // If location exists, use it
  if (location) {
    const addressParts = [
      location.addressLine1 || location.address,
      location.addressLine2 || location.anotherAddress,
      location.city,
      location.state,
      location.zipCode || location.zip
    ].filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    // Fallback to any available address fields
    const fallbackParts = [
      location.name,
      location.addressName,
      location.type
    ].filter(Boolean);

    if (fallbackParts.length > 0) {
      return fallbackParts.join(", ");
    }
  }

  // Fall back to tempLocationData from the ticket
  if (ticket?.tempLocationData) {
    const tempLoc = ticket.tempLocationData;
    const tempParts = [
      tempLoc.city,
      tempLoc.state,
      tempLoc.zip
    ].filter(Boolean);

    if (tempParts.length > 0) {
      return `${tempLoc.city || ''}${tempLoc.state ? ', ' + tempLoc.state : ''}${tempLoc.zip ? ' ' + tempLoc.zip : ''}`.trim();
    }
  }

  return "Address TBD";
};

// Helper function to get service ticket status colors
const getServiceTicketStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "pending":
    case "draft":
      return "bg-gray-100 text-gray-800";
    case "scheduled":
    case "confirmed":
      return "bg-green-100 text-green-800";
    case "in-progress":
    case "started":
      return "bg-purple-100 text-purple-800";
    case "completed":
      return "bg-yellow-100 text-yellow-800";
    case "canceled":
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Helper function to format service ticket status text
const formatServiceTicketStatus = (status: string) => {
  return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase().replace("-", " ");
};



// Helper function to derive status from timestamps when status field is not available
const deriveTicketStatus = (ticket: any): string => {
  if (ticket.status) return ticket.status;

  // Derive from timestamps
  if (ticket.timeOut || ticket.completedAt || ticket.completed_at) {
    return "COMPLETED";
  } else if (ticket.timeIn) {
    return "IN-PROGRESS";
  } else {
    return "SCHEDULED";
  }
};

export const ContainerInfoList: FC<ContainerInfoListProps> = ({
  serviceTickets,
  showHeader = true,
  maxItems,
  className = "",
  bookingId,
  tempClientData,
}) => {
  const navigate = useNavigate();

  const sortedTickets = [...serviceTickets].sort((a, b) => {
    const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
    const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
    return dateB - dateA;
  });
  const displayedTickets = maxItems
    ? sortedTickets.slice(0, maxItems)
    : sortedTickets;
  const remainingCount = maxItems
    ? Math.max(0, sortedTickets.length - maxItems)
    : 0;

  const getServiceTypeColor = (serviceType: ServiceType) => {
    switch (serviceType) {
      case ServiceType.COLLECTION:
        return "text-blue-600 bg-blue-50";
      case ServiceType.DELIVERY:
        return "text-green-600 bg-green-50";
      case ServiceType.FABRICATION_SERVICE:
        return "text-purple-600 bg-purple-50";
      case ServiceType.WAREHOUSE_SERVICE:
        return "text-orange-600 bg-orange-50";
      default:
        return "text-blue-600 bg-gray-50";
    }
  };

  const getServiceIcon = (serviceType: ServiceType) => {
    switch (serviceType) {
      case ServiceType.COLLECTION:
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.WAREHOUSE_SERVICE:
        return <Package size={14} />;
      default:
        return <Package size={14} />;
    }
  };

  if (serviceTickets.length === 0) {
    return (
      <div className={`text-center py-4 text-gray-500 ${className}`}>
        No container information available
      </div>
    );
  }

  return (
    <div className={className}>
      {showHeader && (
        <div className="flex justify-between items-center mb-3">
          <Text strong>Container Information</Text>
          <Text type="secondary" className="text-sm">
            {serviceTickets.length} service
            {serviceTickets.length !== 1 ? "s" : ""}
          </Text>
        </div>
      )}

      <div className="space-y-3">
        {displayedTickets.map((ticket) => {

          const inventoryStats = ticket.inventories
            ? calculateCubicFeetInventory(ticket.inventories)
            : { totalCubicFeet: 'N/A', totalWeight: 0, items: [] };

          // Get the appropriate client based on service type (shipper/consignee for this service)
          const relevantClient = ticket.clientService || ticket.client;
          const clientDisplayName = getClientDisplayName(relevantClient, ticket);
          const serviceAddress = formatServiceAddress(ticket.location, ticket);

          // Add click handler function
          const handleCardClick = () => {
            // Use the passed bookingId prop first, then fall back to ticket's jobTicket id
            const targetBookingId = bookingId || ticket.jobTicket?.id || ticket.jobTicketId || ticket.bookingId;

            if (!targetBookingId) {
              console.warn('No booking ID available for navigation', {
                passedBookingId: bookingId,
                ticket: ticket,
                jobTicket: ticket.jobTicket,
                availableKeys: Object.keys(ticket)
              });
              return;
            }
            navigate(`/bookings/${targetBookingId}?tab=services&serviceId=${ticket.id}`);
          };

          return (
            <div
              key={ticket.id}
              className="border border-gray-300 rounded-lg p-3 bg-white hover:bg-gray-50 transition-all duration-200 cursor-pointer hover:shadow-lg hover:border-blue-400 shadow-sm"
              onClick={handleCardClick}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-2 flex-wrap">
                  {getServiceIcon(ticket.serviceType)}
                  <Text strong className="text-sm">
                    {capitalize(ticket.serviceType)}
                  </Text>
                  <Text strong className="text-sm">
                    #{ticket.serial}
                  </Text>
                  {/* Service Ticket Status - Always show using derived status */}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getServiceTicketStatusColor(deriveTicketStatus(ticket))}`}>
                    {formatServiceTicketStatus(deriveTicketStatus(ticket))}
                  </span>
                </div>
                
                {/* FIXED: Use the proven date/time pattern from Services component */}
                <div className="text-right">
                  <div className="flex items-center space-x-1 text-xs">
                    <Calendar size={12} className="text-gray-400" />
                    <span>
                      {new Date(ticket.serviceDatetime).toLocaleDateString('en-US', { 
                        weekday: 'short',
                        month: 'numeric', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Clock size={12} className="text-gray-400" />
                    <span>
                      {ticket.startDatetime ? dayjs(ticket.startDatetime).format("h:mm A") : "Time TBD"}
                      {ticket.duration && ` • ${ticket.duration}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content Grid */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {/* Location & Client */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="font-medium">
                    {ticket.serviceType === ServiceType.COLLECTION
                      ? "From:"
                      : ticket.serviceType === ServiceType.DELIVERY
                      ? "To:"
                      : "At:"}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {serviceAddress}
                  </div>
                  {clientDisplayName && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500">
                        {ticket.serviceType === ServiceType.COLLECTION
                          ? "Shipper"
                          : ticket.serviceType === ServiceType.DELIVERY
                          ? "Consignee"
                          : "Client"}
                      </div>
                      <div className="text-xs font-medium">
                        {clientDisplayName}
                      </div>
                    </div>
                  )}
                </div>

                {/* Container Details */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Container Info
                  </div>
                  <div className="flex items-center space-x-1">
                    <Package size={12} />
                    <div className="text-sm">
                      {ticket.inventories && ticket.inventories.length > 0 ? (
                        <>
                          {ticket.inventories.length} items • {inventoryStats.totalCubicFeet} ft³
                          {inventoryStats.totalWeight > 0 && (
                            <span> • {inventoryStats.totalWeight} lbs</span>
                          )}
                        </>
                      ) : ticket.estimateTemplates && ticket.estimateTemplates.length > 0 ? (
                        <>
                          <span className="text-purple-600 font-medium">Est: </span>
                          {ticket.estimateTemplates.reduce((sum: number, t: any) => sum + (t.quantity || 0), 0)} pkgs
                          {ticket.estimateTemplates.map((t: any, i: number) => (
                            <span key={i} className="text-gray-500 text-xs ml-1">
                              ({t.quantity}x {t.templateName})
                            </span>
                          ))}
                        </>
                      ) : ticket.estimatedItemCount ? (
                        <>
                          <span className="text-purple-600 font-medium">Est: </span>
                          ~{ticket.estimatedItemCount} items
                        </>
                      ) : (
                        <span className="text-gray-400 italic">No inventory data</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Staff & Vehicle Resources */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Resources</div>
                  {ticket.vessel && (
                    <div className="flex items-center space-x-1 mb-2">
                      <Truck size={12} className="text-gray-600" />
                      <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 rounded">
                        {ticket.vessel.name}
                      </span>
                    </div>
                  )}
                  {ticket.staffs && ticket.staffs.length > 0 && (
                    <div className="flex items-start space-x-1">
                      <Users size={12} className="text-gray-600 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {ticket.staffs.map((staff) => (
                          <span
                            key={staff.id}
                            className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full"
                          >
                            {formatStaffName(staff)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!ticket.vessel && (!ticket.staffs || ticket.staffs.length === 0) && (
                    <span className="text-xs text-gray-400 italic">Not assigned</span>
                  )}
                </div>
              </div>

              {/* Additional Services */}
              {ticket.addOnServices && ticket.addOnServices.length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <div className="flex items-center space-x-2">
                    <Text type="secondary" className="text-xs">
                      Add-ons:
                    </Text>
                    {ticket.addOnServices.map((service, index) => (
                      <span
                        key={index}
                        className="px-1.5 py-0.5 bg-gray-100 rounded text-xs"
                      >
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {remainingCount > 0 && (
          <div className="text-center text-sm text-gray-500 py-2">
            ... and {remainingCount} more service
            {remainingCount !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
};